import { useMemo } from 'react'
import { MRT_Row, MRT_TableInstance } from 'material-react-table'
import { DocumentRow } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useBulkEditValueContext } from './BulkEditValueProvider'

type UseBulkActionCountsOptions = {
  table: MRT_TableInstance<Required<DocumentRow>>
  field: ProjectGridField
}

export default function useBulkActionCounts({
  table,
  field,
}: UseBulkActionCountsOptions) {
  const { fieldsMap } = useProjectContext()
  const { selectedAllDocsCount } = useBulkEditValueContext()

  const parentField = fieldsMap[field.id]?.parentField
  const isTableField = !!parentField
  const tableFieldName = parentField?.name ?? ''

  const selectedRows = useMemo(
    () =>
      table
        .getSelectedRowModel()
        .flatRows.map((row: MRT_Row<Required<DocumentRow>>) => row.original),
    [table],
  )

  const matchingRows = useMemo(
    () =>
      selectedRows.filter(
        (row) => row.project_grid_id === field.project_grid_id,
      ),
    [selectedRows, field.project_grid_id],
  )

  const numberOfSelectedDocs = new Set(
    selectedRows.map((row) => row.document_id),
  ).size

  const rowsOrDocsCount = isTableField
    ? matchingRows.length
    : numberOfSelectedDocs

  // We can't calculate an exact row count, so we just say "All"
  const selectedAllCount = isTableField ? 'All' : selectedAllDocsCount

  const displayCount = selectedAllDocsCount ? selectedAllCount : rowsOrDocsCount

  return {
    selectedRows,
    matchingRows,
    isTableField,
    tableFieldName,
    displayCount,
    numberOfSelectedDocs,
  }
}
